/**
 * Reads an AI search response into props the results table can render (#257).
 *
 * This composable is deliberately dumb. Every display string — the entity
 * title, the column labels, the sentence describing the filter — is composed
 * server-side from `entity.json` and passed through here untouched. The client
 * holds no copy of the schema, so any label it invented would be a second copy
 * that drifts the moment the schema changes, and a filter description that
 * misdescribes the filter defeats the reason for showing one.
 *
 * The count is the one number computed here, and it is computed from the length
 * of the row array — never read out of anything the model wrote. A count taken
 * from model prose is a count nobody verified.
 *
 * Every read degrades to an empty result rather than throwing: a search view
 * that white-screens on an unexpected payload is worse than one that reports
 * finding nothing.
 */

const EMPTY_RESULT = Object.freeze({
    rows: [],
    tableHeaders: [],
    resultCount: 0,
    description: '',
    entityType: '',
});

function emptyResult() {
    // A fresh object each time. Returning the frozen constant would let one
    // caller's mutation leak into every later empty search.
    return { rows: [], tableHeaders: [], resultCount: 0, description: '', entityType: '' };
}

export function useAiSearch() {
    /**
     * @param {object|null|undefined} payload the `data` object from the search endpoint
     */
    function readSearchResponse(payload) {
        if (!payload || typeof payload !== 'object') return emptyResult();

        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        const tableHeaders = Array.isArray(payload.tableHeaders) ? payload.tableHeaders : [];
        const description =
            typeof payload.appliedFilter?.description === 'string'
                ? payload.appliedFilter.description
                : '';

        // The entity type the server actually searched, kept so each row can
        // link to its record. Read from the applied filter rather than inferred
        // from the question or the columns: those are guesses, and a guess here
        // sends the user to another entity's page under the right-looking id.
        const entityType =
            typeof payload.appliedFilter?.entityType === 'string'
                ? payload.appliedFilter.entityType
                : '';

        return {
            rows,
            tableHeaders,
            resultCount: rows.length,
            description,
            entityType,
        };
    }

    /**
     * Names the router's read route for an entity type, or '' if it has none.
     *
     * Resolved against the route table instead of built as `/${type}/${id}`,
     * because the routes themselves already record which entity type each read
     * page fetches. A string template cannot tell that a type has no read page —
     * it produces a plausible URL that matches nothing and lands the user on a
     * blank screen — whereas an absent route here is something the caller can
     * see and leave the row unlinked for.
     *
     * `pageType` is part of the match: the update route for an entity carries the
     * same `entityTypeToFetch`, and a search result opening an edit form is not
     * what the user clicked.
     *
     * @param {string} entityType entity type from the applied filter
     * @param {Array<{name?: string, meta?: object}>} routes typically `router.getRoutes()`
     */
    function readRowRouteName(entityType, routes) {
        if (!entityType || !Array.isArray(routes)) return '';

        const match = routes.find(
            route =>
                route?.meta?.pageType === 'read' &&
                route?.meta?.entityTypeToFetch === entityType,
        );

        return match?.name ?? '';
    }

    return { readSearchResponse, readRowRouteName, EMPTY_RESULT };
}

export default useAiSearch;
